import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { Loader } from "@/components/ui-helpers";
import { Hash, MessageSquarePlus, Lock, X } from "lucide-react";
import { timeAgo, initials } from "@/mobile/mobileUtils";
import { toast } from "sonner";

export default function MobileChat() {
  const nav = useNavigate();
  const [channels, setChannels] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [staff, setStaff] = useState([]);

  const load = () => api.get("/team/channels").then((r) => setChannels(r.data || [])).catch(() => setChannels([]));
  useEffect(() => { load(); }, []);

  const openNew = async () => {
    setShowNew(true);
    if (staff.length === 0) api.get("/team/staff").then((r) => setStaff(r.data || [])).catch(() => {});
  };
  const startDm = async (uid) => {
    try { const { data } = await api.post("/team/dm", { user_id: uid }); setShowNew(false); nav(`/m/chat/${data.id || data._id}`); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (!channels) return <Loader />;
  const chans = channels.filter((c) => c.kind === "channel");
  const dms = channels.filter((c) => c.kind === "dm");

  return (
    <div className="p-4 space-y-4" data-testid="mobile-chat">
      <div className="flex items-center justify-between">
        <h1 className="font-head font-extrabold text-xl text-slate-900">Team Chat</h1>
        <button onClick={openNew} data-testid="chat-new-dm" className="text-primary inline-flex items-center gap-1 text-sm font-semibold"><MessageSquarePlus className="h-5 w-5" /></button>
      </div>

      <div>
        <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5">Channels</div>
        <div className="space-y-2">
          {chans.map((c) => <Row key={c.id} c={c} icon={c.private ? Lock : Hash} onClick={() => nav(`/m/chat/${c.id}`)} />)}
        </div>
      </div>

      <div>
        <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5">Direct messages</div>
        <div className="space-y-2">
          {dms.length === 0 && <div className="text-sm text-slate-400">No direct messages yet.</div>}
          {dms.map((c) => <Row key={c.id} c={c} avatar onClick={() => nav(`/m/chat/${c.id}`)} />)}
        </div>
      </div>

      {showNew && (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-end" onClick={() => setShowNew(false)}>
          <div className="w-full max-w-md mx-auto bg-white rounded-t-3xl p-5 max-h-[70vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="chat-new-sheet">
            <div className="flex items-center justify-between mb-3"><h3 className="font-head font-bold">Start a conversation</h3><button onClick={() => setShowNew(false)}><X className="h-5 w-5 text-slate-400" /></button></div>
            <div className="space-y-1.5">
              {staff.map((s) => (
                <button key={s.id} onClick={() => startDm(s.id)} data-testid={`chat-dm-${s.username || s.id}`} className="w-full flex items-center gap-3 p-2.5 rounded-xl hover:bg-slate-50 text-left">
                  <div className="h-9 w-9 rounded-full bg-secondary text-white text-xs font-bold flex items-center justify-center">{initials(s.name)}</div>
                  <div><div className="text-sm font-semibold text-slate-800">{s.name}</div><div className="text-xs text-slate-400">{s.title || s.role}</div></div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ c, icon: Icon, avatar, onClick }) {
  return (
    <button onClick={onClick} data-testid={`chat-open-${c.id}`} className="w-full bg-white rounded-2xl p-3.5 border border-slate-100 flex items-center gap-3 text-left active:scale-[0.99] transition-transform">
      {avatar
        ? <div className="h-10 w-10 rounded-full bg-secondary text-white text-xs font-bold flex items-center justify-center shrink-0">{initials(c.name)}</div>
        : <div className="h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0"><Icon className="h-5 w-5" /></div>}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <div className="font-semibold text-slate-800 truncate">{c.name}</div>
          {c.last_at && <div className="text-[10px] text-slate-400 shrink-0">{timeAgo(c.last_at)}</div>}
        </div>
        <div className="text-xs text-slate-400 truncate">{c.description || (c.kind === "dm" ? "Direct message" : "Channel")}</div>
      </div>
      {c.unread > 0 && <span className="bg-primary text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center shrink-0" data-testid="chat-unread">{c.unread}</span>}
    </button>
  );
}
